import { memo, useMemo } from 'react';
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
  type FieldValues,
} from 'react-hook-form';
import {
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { DatePicker } from '@/components/ui/date-picker';
import { cn } from '@/lib/utils';
import { FieldInfoButton } from './FieldInfoButton';
import { formatDateToItDateInput, parseItDateInputToDate } from '@/utils/date';
import type { FieldConfig } from './entityFormTypes';
import { CheckboxRow, FormField, FormGrid, FormSection } from './FormLayout';
import { statusToneClasses, statusToneInteractiveFieldClasses } from '@/lib/statusTone';
import { MonthYearField } from './MonthYearField';
import { YearField } from './YearField';

// ============================================================================
// SAFE MODE IMPORTS
// These provide native fallbacks for users experiencing UI component crashes.
// Can be removed once underlying Radix/portal issues are fully resolved.
// ============================================================================
import { useIsSafeMode } from '@/lib/safeMode';
import { SafeSelect } from '@/components/ui/safe-select';
import { SafeDatePicker } from '@/components/ui/safe-date-picker';
// ============================================================================

interface FormSectionConfig {
  readonly title?: string;
  readonly description?: string;
  readonly fields: readonly string[];
}

interface EntityFormFieldsProps {
  fields: readonly FieldConfig[];
  sections?: readonly FormSectionConfig[];
  disabled?: boolean;
}

function useFieldMap(fields: readonly FieldConfig[]) {
  return useMemo(() => new Map(fields.map((field) => [field.key, field])), [fields]);
}

const EntityFormFields = memo(function EntityFormFields({
  fields,
  sections,
  disabled = false,
}: EntityFormFieldsProps) {
  const fieldMap = useFieldMap(fields);

  if (sections && sections.length > 0) {
    return (
      <div className="space-y-4">
        {sections.map((section) => (
          <FormSection
            key={section.title ?? section.fields.join('-')}
            title={section.title}
            description={section.description}
          >
            <FormGrid>
              {section.fields.map((key) => {
                const field = fieldMap.get(key);
                if (!field) return null;
                return <EntityFormField key={field.key} field={field} disabled={disabled} />;
              })}
            </FormGrid>
          </FormSection>
        ))}
      </div>
    );
  }

  return (
    <FormGrid>
      {fields.map((field) => (
        <EntityFormField key={field.key} field={field} disabled={disabled} />
      ))}
    </FormGrid>
  );
});
EntityFormFields.displayName = 'EntityFormFields';

export { EntityFormFields };

function EntityFormField({ field, disabled }: { field: FieldConfig; disabled: boolean }) {
  const { register, control, setValue, trigger, getValues } = useFormContext<FieldValues>();
  const { errors } = useFormState({ name: field.key });
  const fieldError = (errors as Record<string, { message?: string }>)[
    field.key
  ]?.message?.toString();
  const watchedValue = useWatch({ name: field.key });
  const rawValue =
    field.type === 'date' || field.type === 'month-year' || field.type === 'year'
      ? (watchedValue ?? '')
      : '';
  const dependencyKeys = field.dependsOn ?? [];
  useWatch({ name: dependencyKeys });
  const isRequired = field.required;
  const isDisabled =
    typeof field.disabled === 'function'
      ? Boolean(field.disabled(getValues()))
      : Boolean(field.disabled);
  const resolvedDisabled = isDisabled || disabled;
  const errorTone = statusToneClasses('danger');
  const errorInputClasses = fieldError ? cn(...statusToneInteractiveFieldClasses('danger')) : '';
  const reducedShadowClasses = 'shadow-none hover:shadow-none focus:shadow-none';
  const colSpan = field.type === 'textarea' ? 2 : (field.colSpan ?? 1);

  // SAFE MODE: Use native fallbacks when enabled
  const isSafeMode = useIsSafeMode();

  const labelNode = field.tooltip ? (
    <FieldInfoButton content={field.tooltip} label={field.label} />
  ) : (
    field.label
  );

  if (field.type === 'checkbox') {
    return (
      <div className={cn('space-y-1', colSpan === 2 ? 'md:col-span-2' : null)}>
        <CheckboxRow
          id={field.key}
          label={labelNode}
          description={field.description}
          required={isRequired}
        >
          <Controller
            control={control}
            name={field.key}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                id={field.key}
                checked={Boolean(value)}
                onCheckedChange={onChange}
                disabled={resolvedDisabled}
              />
            )}
          />
        </CheckboxRow>
        {fieldError ? (
          <p className={cn('text-sm', errorTone.text)} role="alert">
            {fieldError}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <FormField
      label={labelNode}
      htmlFor={field.key}
      required={isRequired}
      description={field.description}
      error={fieldError}
      colSpan={colSpan}
    >
      {field.type === 'textarea' ? (
        <Textarea
          id={field.key}
          {...register(field.key)}
          placeholder={field.placeholder}
          disabled={resolvedDisabled}
          className={cn(errorInputClasses, reducedShadowClasses, 'resize-none')}
          rows={4}
        />
      ) : field.type === 'date' ? (
        // SAFE MODE: Use SafeDatePicker when enabled, standard DatePicker otherwise
        isSafeMode ? (
          <SafeDatePicker
            date={rawValue ? parseItDateInputToDate(String(rawValue)) : undefined}
            onSelect={(date) => {
              const formattedDate = date ? formatDateToItDateInput(date) : '';
              setValue(field.key, formattedDate, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            placeholder={field.placeholder || 'Seleziona data'}
            className={cn('w-full', errorInputClasses, reducedShadowClasses)}
            disabled={resolvedDisabled}
            forceNative
          />
        ) : (
          <DatePicker
            date={rawValue ? parseItDateInputToDate(String(rawValue)) : undefined}
            onSelect={(date) => {
              const formattedDate = date ? formatDateToItDateInput(date) : '';
              setValue(field.key, formattedDate, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            onTextChange={(value) => {
              setValue(field.key, value, {
                shouldDirty: true,
                shouldValidate: false,
              });
            }}
            onBlur={() => {
              if (!rawValue) return;
              const parsed = parseItDateInputToDate(String(rawValue));
              if (!parsed) {
                void trigger(field.key);
                return;
              }
              const formatted = formatDateToItDateInput(parsed);
              if (formatted !== rawValue) {
                setValue(field.key, formatted, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              } else {
                void trigger(field.key);
              }
            }}
            placeholder={field.placeholder || 'Seleziona data'}
            className="w-full"
            inputClassName={cn(errorInputClasses, reducedShadowClasses)}
            disabled={resolvedDisabled}
          />
        )
      ) : field.type === 'month-year' ? (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { value, onChange } }) => (
            <MonthYearField
              id={field.key}
              value={value ? String(value) : ''}
              onChange={onChange}
              placeholder={field.placeholder}
              disabled={resolvedDisabled}
              error={Boolean(fieldError)}
            />
          )}
        />
      ) : field.type === 'year' ? (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { value, onChange } }) => (
            <YearField
              id={field.key}
              value={value ? String(value) : ''}
              onChange={onChange}
              placeholder={field.placeholder}
              disabled={resolvedDisabled}
              error={Boolean(fieldError)}
            />
          )}
        />
      ) : field.type === 'select' ? (
        // SAFE MODE: Use SafeSelect when enabled, standard Select otherwise
        isSafeMode ? (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { value, onChange } }) => (
              <SafeSelect
                value={value ? String(value) : undefined}
                onValueChange={onChange}
                options={field.options ?? []}
                placeholder={field.placeholder ?? "Seleziona un'opzione"}
                disabled={resolvedDisabled}
                className={cn(errorInputClasses, reducedShadowClasses)}
                forceNative
              />
            )}
          />
        ) : (
          <Controller
            control={control}
            name={field.key}
            render={({ field: { value, onChange } }) => (
              <Select
                onValueChange={onChange}
                value={value ? String(value) : undefined}
                disabled={resolvedDisabled}
              >
                <SelectTrigger
                  id={field.key}
                  className={cn(errorInputClasses, reducedShadowClasses)}
                >
                  <SelectValue placeholder={field.placeholder ?? "Seleziona un'opzione"} />
                </SelectTrigger>
                <SelectContent>
                  {field.options?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )
      ) : (
        <Input
          id={field.key}
          type={field.type}
          {...register(field.key)}
          placeholder={field.placeholder}
          disabled={resolvedDisabled}
          className={cn(errorInputClasses, reducedShadowClasses)}
        />
      )}
    </FormField>
  );
}
